import ActionPerformer from "@/utils/moreMenu/ActionPerformer.ts";
import {ActionPlaceholder} from "@/utils/moreMenu/ActionHandler.ts";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import ActionPerformerUtil from "@/utils/moreMenu/ActionPerformerUtil.ts";
import {useErrorsStore, useMessagesStore} from "@/stores/ToastDataStore.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import BaseDispatcher from "@/utils/presenters/BaseDispatcher.ts";
import {Dialog} from "quasar";
import {defineAsyncComponent} from "vue";

const DashboardEditorDialog = defineAsyncComponent(() => import("@/components/dialogs/DashboardEditorDialog.vue"));
const ChartConfigEditorDialog = defineAsyncComponent(() => import("@/components/bi/ChartConfigEditorDialog.vue"));
const VirtualEntityEditorDialog = defineAsyncComponent(() => import("@/components/virtualEntity/VirtualEntityEditorDialog.vue"));
import {DashboardDialogPayload} from "@/utils/presenters/interfaces.ts";
import Translator from "@/utils/common/Translator.ts";
import Base64 from "@/utils/urlUtils/Base64.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import {useLoadingStore} from "@/stores/LoadingStore.ts";
import NeoleapResponseUtil from "@/utils/NeoleapResponseUtil.ts";
import GeideaResponseUtil from "@/utils/GeideaResponseUtil.ts";
import {SimpleEntityMenuItem} from "@/utils/UIConstants/MoreMenuItem.ts";
import CustomReportLauncherAction from "@/utils/editView/editScreens/CustomReportLauncherAction.ts";
import PerformActionReq = namaTypes.PerformActionReq;
import EInvoiceSignResponse = namaTypes.EInvoiceSignResponse;
import ACCEntities from "@/utils/constants/ACCEntities.ts";
import FieldToFocusUtils from "@/utils/common/FieldToFocusUtils.ts";
import IdsAndConstants from "@/utils/UIConstants/IdsAndConstants.ts";

export default class EditViewPerformer extends ActionPerformer
{
  private presenter: EditPresenter;

  constructor(presenter: EditPresenter) {
    super();
    this.presenter = presenter;
  }

  override async calcDefaultsOf(action: ActionPlaceholder, callback: (fields: namaTypes.DTOFilledQuestionField[]) => void) {
    await ActionPerformerUtil.calcDefaultsOf(action, callback, this.presenter.dispatcher!);
  }

  override getSearchContextProvider(): BaseDispatcher | undefined {
    return this.presenter.dispatcher!;
  }

  override isRecordNotSaved(): boolean {
    return this.presenter.isInNewMode() || this.presenter.hasChanges();
  }

  override editDashboardElements(): void {
    const dashboardRecord = this.presenter.fetchRecord();
    const kind = NamaCommon.getFieldValue(dashboardRecord, 'kind');
    if (kind === 'Tabbed') {
      useErrorsStore().pushErrorStr(Translator.translate('biTabbedDashboardCannotHaveWidgets'));
      return;
    }
    Dialog.create({
      component: DashboardEditorDialog,
      componentProps: {
        dashboardRecord: dashboardRecord
      }
    }).onOk(async (payload: DashboardDialogPayload) => {
      const record = this.presenter.fetchRecord();
      NamaCommon.setFieldValue(record, 'rowsCount', payload.row);
      NamaCommon.setFieldValue(record, 'colsCount', payload.column);
      NamaCommon.setFieldValue(record, 'totalDashboardRowsCount', payload.rowsCount);

      const oldCharts: any[] = NamaCommon.getFieldValue(record, 'charts') ?? [];
      const oldByWidgetId = new Map<string, any>();
      for (const row of oldCharts)
        if (row?.element?.id)
          oldByWidgetId.set(row.element.id, row);

      const newCharts: any[] = [];
      for (const el of payload.elements) {
        let row = oldByWidgetId.get(el.id);
        if (!row) {
          const newId = await ClientCache.asyncNextGUIDId();
          row = {
            id: newId,
            g2Id: newId,
            element: {
              id: el.id,
              code: el.widgetCode,
              name1: el.widgetName1,
              name2: el.widgetName2,
              entityType: 'DashBoardWidget',
            },
          };
        }
        row.columnNumber = el.column;
        row.rowNumber = el.row;
        row.widthInColumns = el.columnSpan;
        row.heightInRows = el.rowSpan;
        newCharts.push(row);
      }
      NamaCommon.setFieldValue(record, 'charts', newCharts);
    });
  }

  override editChartConfig(): void {
    const record = this.presenter.fetchRecord();
    Dialog.create({
      component: ChartConfigEditorDialog,
      componentProps: {
        widgetRecord: record,
        dashboardId: NamaCommon.getFieldValue(record, 'dashboard') ?? ''
      }
    }).onOk((chartConfigJSON: string) => {
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'chartConfigJSON', chartConfigJSON);
    });
  }

  override editMappings(): void {
    const record = this.presenter.fetchRecord();
    Dialog.create({
      component: VirtualEntityEditorDialog,
      componentProps: {
        entityRecord: record,
      }
    }).onOk((payload: {sqlQuery: string; mappingJson: string}) => {
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'sqlQuery', payload.sqlQuery);
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'mappingJson', payload.mappingJson);
    });
  }

  override async fetchLastTerminalPayTrnx(): Promise<void> {
    await this.pgwTransaction(false);
  }

  override async payInvoice(paymentValue: number): Promise<void> {
    await this.pgwTransaction(true, paymentValue);
  }

  private async pgwTransaction(payment: boolean, paymentValue?: number) {
    let port = "7842";
    let paymentLines: Record<string, any>[] = NamaCommon.getFieldValue(this.presenter.fetchRecord(), "paymentLines");
    let line = this.fetchOrAddDataItem(paymentLines);
    let valueToPaid: number = paymentValue || line.paymentValue || NamaCommon.getFieldValue(this.presenter.fetchRecord(), this.calcAmountFieldByEntityType());
    if (ObjectChecker.isNullOrUndefined(valueToPaid) || valueToPaid < 0)
      return;
    if (payment) {
      let pgwTransactionId = await ClientCache.asyncNextGUIDId() ?? ""
      localStorage.setItem('nm-last-pg-Id', pgwTransactionId);
      await this.handlePGWRequest(payment, pgwTransactionId, port, valueToPaid, paymentLines, line);
    } else {
      let lastPgwTransactionId: string = localStorage.getItem('nm-last-pg-Id') ?? '';
      await this.handlePGWRequest(payment, lastPgwTransactionId, port, valueToPaid, paymentLines, line);
    }
  }

  private calcAmountFieldByEntityType() {
    if (ObjectChecker.isAnyEqualToFirst(this.presenter.entityType, ACCEntities.PaymentVoucher, ACCEntities.PaymentOrder,
      ACCEntities.ReceiptVoucher, ACCEntities.ReceiptOrder, ACCEntities.PaymentOrder, ACCEntities.BankTransfer))
      return "amount.value.amount";
    return "money.remaining";
  }

  private async handlePGWRequest(payment: boolean, lastPgwTransactionId: string, port: string, valueToPaid: number, paymentLines: Record<string, any>[], line: Record<string, any>) {
    if (!payment && !lastPgwTransactionId) {
      useErrorsStore().pushErrorStr(Translator.translateId("There is no uncompleted transaction "));
      return;
    }
    let paymentTerminalRef = NamaCommon.getFieldValue(this.presenter.fetchRecord(), "paymentTerminal");
    if (!paymentTerminalRef) {
      useErrorsStore().pushErrorStr(Translator.translateId("Please select payment terminal"));
      return;
    }
    let url = "http://localhost:" + port + "/payInvoice/" + valueToPaid + "/" + lastPgwTransactionId;
    if (!payment)
      url = "http://localhost:" + port + "/lastTrnx/" + lastPgwTransactionId;
    try {
      let response: any = await NamaCommon.namaFetch(url, {doNotUseMainReqForHttp: true,
        method : 'get'});
      console.log('handlePGWRequest', response);
      await this.processPayResponse(response, valueToPaid, paymentLines.indexOf(line), port);
    } catch (e) {
      useErrorsStore().pushErrorStr(Translator.translateId("There is no connection on port " + port
        + ", please make sure that the PGW server is running. You can download the setup file from <a href='https://www.namasoft.com/bin/PGWSetup.msi' target='_blank'>here</a>"
        + "<br/>If you are using chrome make sure that the option <a target='_blank' href='chrome://flags/#block-insecure-private-network-requests'>chrome://flags/#block-insecure-private-network-requests</a> is disabled"));
    }
  }

  private async processPayResponse(pgwResp: any, valueToPaid: number, selectedRow: number, port: string) {
    let paymentTerminalRef = NamaCommon.getFieldValue(this.presenter.fetchRecord(), "paymentTerminal");
    let request = {pgwResp: JSON.stringify(pgwResp), valueToPaid: valueToPaid, terminal: JSON.stringify(paymentTerminalRef)};
    try {
      let respLine: any = await NamaCommon.namaFetch(NamaURLS.PreparePGWLine, {params: request});
      console.log('processPayResponse', respLine);
      if (!respLine)
        return;
      let line = {
        merchantId: respLine.pgwProperties.merchantId,
        ecrRefNum: respLine.pgwProperties.ecrRefNum,
        schemeId: respLine.pgwProperties.schemeId,
        panNum: respLine.pgwProperties.panNum,
        terminalId: respLine.pgwProperties.terminalId,
        stanNum: respLine.pgwProperties.stanNum,
        authCode: respLine.pgwProperties.authCode,
        respCode: respLine.pgwProperties.respCode,
        de55Response: respLine.pgwProperties.de55Response,
        cardType: respLine.pgwProperties.cardType,
        maskedCardNumber: respLine.pgwProperties.maskedCardNumber,
        paidFromTerminal: respLine.pgwProperties.paidFromTerminal
      };
      let failureMsg = "Purchase Transaction refused";
      let failureRespCode = false;
      if (line.respCode) {
        let refuseReason = line.respCode.length == 2 ? NeoleapResponseUtil.respCodeWithErrorMsg(line.respCode) :
          GeideaResponseUtil.respCodeWithErrorMsg(line.respCode);
        if (refuseReason) {
          failureRespCode = true;
          failureMsg += " - " + line.respCode + " - " + refuseReason;
        }
      }
      if (failureRespCode || (!line.cardType && (!line.authCode || /^0+$/.test(line.authCode)))) {
        useErrorsStore().pushErrorStr(Translator.translateId(failureMsg));
        return;
      }
      if (ObjectChecker.areAllEmptyOrNull(line.merchantId, line.ecrRefNum, line.schemeId, line.terminalId, line.stanNum, line.authCode, line.panNum, line.de55Response, line.cardType, line.maskedCardNumber)) {
        useErrorsStore().pushErrorStr(Translator.translateId("Purchase Transaction not completed successfully"));
        return;
      }
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'paymentLines.pgwProperties.merchantId', line.merchantId, selectedRow);
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'paymentLines.pgwProperties.ecrRefNum', line.ecrRefNum, selectedRow);
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'paymentLines.pgwProperties.panNum', line.panNum, selectedRow);
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'paymentLines.pgwProperties.schemeId', line.schemeId, selectedRow);
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'paymentLines.pgwProperties.terminalId', line.terminalId, selectedRow);
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'paymentLines.pgwProperties.de55Response', line.de55Response, selectedRow);
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'paymentLines.pgwProperties.cardType', line.cardType, selectedRow);
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'paymentLines.pgwProperties.maskedCardNumber', line.maskedCardNumber, selectedRow);
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'paymentLines.pgwProperties.stanNum', line.stanNum, selectedRow);
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'paymentLines.pgwProperties.paidFromTerminal', line.paidFromTerminal, selectedRow);
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'paymentLines.authorizationNumber', line.authCode, selectedRow);
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'paymentLines.paymentMethod', respLine.paymentMethod, selectedRow);
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'paymentLines.paymentValue', valueToPaid, selectedRow);
      useLoadingStore().startLoading();
      await this.presenter.fetchDispatcher().validateColumn('paymentLines.paymentValue', selectedRow, false);
      await FieldToFocusUtils.focusToField(new FieldToFocusUtils(this.presenter), "paymentLines", IdsAndConstants.MRN_REFRESH_CELLS);
      useLoadingStore().stopLoading();
    } catch (e) {
      useErrorsStore().pushErrorStr(Translator.translateId("There is no connection on port " + port + ", please make sure that the PGW server is running. You can download the setup file from <a href='https://www.namasoft.com/bin/PGWSetup.msi' target='_blank'>here</a>"
        + "<br/>If you are using chrome make sure that the option <a target='_blank' href='chrome://flags/#block-insecure-private-network-requests'>chrome://flags/#chrome://flags/#block-insecure-private-network-requests</a> is disabled"));
    }
  }

  private fetchOrAddDataItem(paymentLines: Record<string, any>[]) {
    let mustAddLine = false;
    if (!paymentLines.length)
      mustAddLine = true;
    if (!mustAddLine)
      return paymentLines[paymentLines.length - 1];
    this.presenter.appendRow("paymentLines");
    return NamaCommon.getFieldValue(this.presenter.fetchRecord(), "paymentLines", 0);
  }

  override async readFirstWeight(): Promise<void> {
    await this.readScaleWeight(true);
  }

  override async readSecondWeight(): Promise<void> {
    await this.readScaleWeight(false);
  }

  private async readScaleWeight(readFirstWeight: boolean) {
    let port = "9494";
    let url = "http://localhost:" + port + "/readScaleWeight";
    try {
      const data = await fetch(url);
      const response = await data.json();
      console.log('readScaleWeight', response);
      await this.processReadScaleWeightResponse(readFirstWeight, response, port);
    } catch (e) {
      useErrorsStore().pushErrorStr(Translator.translateId("There is no connection on port " + port
        + ", please make sure that the Weight Scale server is running."
        + "<br/>If you are using chrome make sure that the option <a target='_blank' href='chrome://flags/#block-insecure-private-network-requests'>chrome://flags/#block-insecure-private-network-requests</a> is disabled"));
    }
  }

  private async processReadScaleWeightResponse(readFirstWeight: boolean, weightRes: any, port: string) {
    try {
      let fieldId: any = "details.grossWeight";
      if (!readFirstWeight)
        fieldId = "details.emptyWeight";
      let details: Record<string, any>[] = NamaCommon.getFieldValue(this.presenter.fetchRecord(), "details");
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), fieldId, weightRes, details.length - 1);
      useLoadingStore().startLoading();
      await this.presenter.fetchDispatcher().validateColumn(fieldId, details.length - 1, false);
      useLoadingStore().stopLoading();
    } catch (e) {
      useErrorsStore().pushErrorStr(Translator.translateId("There is no connection on port " + port + ", please make sure that the Weight Scale server is running."
        + "<br/>If you are using chrome make sure that the option <a target='_blank' href='chrome://flags/#block-insecure-private-network-requests'>chrome://flags/#chrome://flags/#block-insecure-private-network-requests</a> is disabled"));
    }
  }

  override async performAction(action: ActionPlaceholder, questionFields: namaTypes.DTOFilledQuestionField[]): Promise<void> {
    await ActionPerformerUtil.performAction(action, questionFields, this.presenter.metadata!, this.presenter.dispatcher!)
  }

  override async runCustomAction(action: ActionPlaceholder): Promise<void> {
    //   in view
    if (!action.customization)
      return;
    if (action.customization.guiPostActionsId) {
      // this.presenter.runCustomGuiPostAction();
      return;
    }
    if (action.customization.reportInfo && action.customization.reportInfo.reportId) {
      let menuItem = new CustomReportLauncherAction(this.presenter, action.customization.reportInfo);
      if (menuItem.executeFn)
        menuItem.executeFn();
      return;
    }
    if (this.isRecordNotSaved()) {
      useErrorsStore().pushErrorStr(Translator.translateId("recordMustBeSaved"));
    }
    let reqData: PerformActionReq = {
      actionInfo: action.customization,
      targetRecords: [{
        entityType: this.presenter.entityType,
        id: NamaCommon.getFieldValue(this.presenter.fetchRecord(), CommonFieldIds.ID, 0)
      }]
    };
    let result = await NamaCommon.namaFetch(NamaURLS.PERFORM_Custom_action, {data: reqData});
    if (result && result.data)
      await this.presenter.onCustomActionSuccess(result.data)
  }

  override async signDocuments(onlySelected: boolean): Promise<void> {
    if (this.presenter.hasChanges()) {
      useErrorsStore().pushErrorStr(Translator.translateId("recordMustBeSaved"));
      return;
    }
    let docId: string = NamaCommon.getFieldValue(this.presenter.fetchRecord(), CommonFieldIds.ID);
    let req: Record<string, any> = {submissionDocId: docId, onlySelected};
    let result: EInvoiceSignResponse[] | any = await NamaCommon.namaFetch(NamaURLS.UpdateTaxAuthJson, {params: req});
    console.log('signDocuments', result);
    if (!result || result.length < 1) {
      useErrorsStore().pushErrorStr(Translator.calcByLanguage(
        "الرجاء التأكد من تفعيل أوبشن إرسال المستندات موقعه إلكترونيا الموجود في إعدادت مصلحة الضرائب أو إنك تحاول توقيع سند إلغاء",
        "Please mark option Send Documents With Signature in tax payer config or you try to sign cancel document"));
      return;
    }
    for (let response of result) {
      await this.executeSignCall(response, docId, onlySelected);
    }
  }

  private async executeSignCall(result: EInvoiceSignResponse, id: string, onlySelected: boolean) {
    const eSignLinks = ", please make sure that the e-signer server is running. <br/>You can download the setup file from <a href='https://www.namasoft.com/bin/eSignerSetup.msi'>here</a>"
      + "E SEAL: <br/><a href='https://preprod.invoicing.eta.gov.eg/ItidaWebSign_3.13.000.exe' target='_blank'>ITIDA Web Sign Version 3.11</a>"
      + "<br/><a href='https://egypttrust.com/wp-content/uploads/2023/08/ePass2003__Activation_EgyptTrust.zip' target='_blank'>Egypt Trust Activation</a>"
      + "<br/>MCDR: <a href='http://www.mcdr-ca.com/AWP.aspx'>MCDR Settings</a>"
      + "<br/>If you are using chrome make sure that the option <a target='_blank' href='chrome://flags/#block-insecure-private-network-requests'>chrome://flags/#block-insecure-private-network-requests</a> is disabled";

    let port = 7994;
    let url = "http://localhost:" + port;
    if (result.einvoiceSignerUrl)
      url = result.einvoiceSignerUrl;
    if (!url.endsWith('/'))
      url += '/';
    url += "signDocuments";
    let request = {
      actualData: result.jsonData,
      authorization: {}
    }
    if (result.einvoiceSignerPin)     //TODO:: Base64.toBase64()
      request.authorization = Base64.encode(`${result.einvoiceSignerType}:${result.einvoiceSignerPin}`);
    try {
      let response: any = await NamaCommon.namaFetch(url, {data: request, doNotUseMainReqForHttp: true});
      console.log('executeSignCall', response);
      await this.processSignDocsResponse(response, id, onlySelected);
    } catch (e) {
      useErrorsStore().pushErrorStr(Translator.translateId("There is no connection on port " + port + eSignLinks));
    }
  }

  private async processSignDocsResponse(response: any, id: string, onlySelected: boolean) {
    let req: Record<string, any> = {
      responseBody: response,
      submissionDocId: id,
      onlySelected: onlySelected
    };
    let data = await NamaCommon.namaFetch(NamaURLS.SignDocuments, {params: req});
    console.log('processSignDocsResponse', data);
    if (data != null) {
      useErrorsStore().pushErrorStr(data);
      return;
    }
    useMessagesStore().pushMessageStr(Translator.translateId("Documents were signed successfully"));
    await this.presenter.refresh(false, false);
  }

  override async showCurrentApprovalCase() {
    await SimpleEntityMenuItem.showCurrentApprovalCasePopup(this.presenter.fetchRecord().currentApprovalCase);
  }
}
